import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useOnboardingData } from "../context/OnboardingDataContext";
import { API_METHODS } from "../../constants";
import useApi from "../hooks/useApi";
import { Spin } from "antd";
import { useLanguage } from "../hooks/useLanguage";
import { useErrorModal } from "../context/ErrorModalContext";

const DecryptionHandler = () => {
  const { updateOnboardingData, clearOnboardingData, onboardingData } =
    useOnboardingData();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [decryptionPayloadResponse, decryptionPayloadRequest] = useApi(
    `/decrypt-payload`,
    null,
    API_METHODS.POST
  );
  const [checkRegistrationsResponse, checkRegistrationsRequest] = useApi(
    `/check-registrations`,
    null,
    API_METHODS.GET
  );

  const { showError } = useErrorModal();
  const pendingPayload = useRef(null);

  useEffect(() => {
    const search = location.search || "";
    const payloadMatch = search.match(/payload=([^&]*)/);
    let encryptedPayload =
      payloadMatch?.[1] ||
      (onboardingData?.preservePayload === true
        ? onboardingData?.encryptedPayload
        : null) ||
      null;
    if (!encryptedPayload || encryptedPayload.trim() === "") {
      // If no payload, navigate here and return immediately
      navigate("/digital-onboarding", { replace: true });
      return;
    }
    clearOnboardingData();
    encryptedPayload = decodeURIComponent(encryptedPayload);
    pendingPayload.current = encryptedPayload;
    let data = {
      payload: encryptedPayload,
    };
    decryptionPayloadRequest(data);
  }, [location.search, navigate]);

  useEffect(() => {
    if (decryptionPayloadResponse.isError && decryptionPayloadResponse.error) {
      showError({
        message: decryptionPayloadResponse.error.displayMessage,
        buttons: [
          {
            label: t("verification.btnExitLabel"),
            variant: "secondary",
            onClick: () => {
              navigate("/digital-onboarding");
            },
          },
        ],
      });

      return;
    }

    if (
      decryptionPayloadResponse.data &&
      !decryptionPayloadResponse.isLoading
    ) {
      updateOnboardingData({
        encryptedPayload: pendingPayload.current,
        msisdn: decryptionPayloadResponse.data.msisdn,
        otp: decryptionPayloadResponse.data.otp,
        channelId: decryptionPayloadResponse.data.channelId,
        simType: decryptionPayloadResponse.data.simType,
      });
      if (decryptionPayloadResponse.data.otp == false) {
        checkRegistrationsRequest(
          null,
          API_METHODS.GET,
          `/check-registrations?msisdn=${decryptionPayloadResponse.data.msisdn}`
        );
      } else {
        // Flags this as a payload arrival, not an exit — home must keep the
        // session /decrypt-payload just minted, since the step-one payload
        // branch sends msisdn alone and relies on its channelId/simType.
        navigate("/digital-onboarding", { state: { fromPayload: true } });
      }
    }
  }, [decryptionPayloadResponse]);

  useEffect(() => {
    if (
      checkRegistrationsResponse.isError &&
      checkRegistrationsResponse.error
    ) {
      if (checkRegistrationsResponse.error.errorCode == "ALREADY_REGISTERED") {
        showError({
          message: checkRegistrationsResponse.error.displayMessage,
          buttons: [
            {
              label: t("verification.btnExitLabel"),
              variant: "secondary",
              onClick: () => {
                navigate("/digital-onboarding");
              },
            },
          ],
        });
      } else {
        showError({
          message: checkRegistrationsResponse.error.displayMessage,
          buttons: [
            {
              label: t("verification.btnExitLabel"),
              variant: "secondary",
              onClick: () => {
                navigate("/digital-onboarding");
              },
            },
          ],
        });
      }
    } else if (
      checkRegistrationsResponse.data &&
      !checkRegistrationsResponse.isLoading
    ) {
      if (checkRegistrationsResponse.data.exists == true) {
        showError({
          message: "The mobile number you entered is already registered.",
          buttons: [
            {
              label: t("verification.btnExitLabel"),
              variant: "secondary",
              onClick: () => {
                navigate("/digital-onboarding");
              },
            },
          ],
        });
      } else {
        updateOnboardingData({
          checkRegistrationsData: checkRegistrationsResponse.data,
        });
        navigate("/digital-onboarding/step-two");
      }
    }
  }, [checkRegistrationsResponse]);

  if (decryptionPayloadResponse.isLoading || checkRegistrationsResponse.isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <Spin size="large" />
      </div>
    );
  }
};

export default DecryptionHandler;
